/** 主布局：左侧导航 + 顶部工具条 + 路由出口 */

import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Layout, Skeleton, Tooltip } from 'antd';
import { KeyOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { SideNav } from './SideNav';
import { ApiKeyModal } from './ApiKeyModal';
import { ThemeSwitch } from './ThemeSwitch';
import { UserMenu } from './UserMenu';
import { ErrorBoundary } from '@/components';
import { useSettingsStore } from '@/store/settings';
import { unauthorizedAction, useAuthStore } from '@/store/auth';
import { setUnauthorizedHandler } from '@/api/client';
import { getHealth } from '@/api/ops';
import { usePolling } from '@/hooks/usePolling';
import './shell.css';

const { Sider, Content } = Layout;

const SIDER_WIDTH = 232;
const SIDER_COLLAPSED = 60;

export function MainLayout() {
  const collapsed = useSettingsStore((s) => s.siderCollapsed);
  const toggleSider = useSettingsStore((s) => s.toggleSider);

  const [keyModal, setKeyModal] = useState<{ open: boolean; reason?: string }>({ open: false });

  const navigate = useNavigate();
  const location = useLocation();
  const authUser = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);

  // client.ts 不认识路由，401 的处理注入在这里。
  //
  // 两条分支不能合并：登录会话过期了要送去登录页；而用 API Key 调试的场景下
  // 根本没有会话可言，把人送去登录页只会让他填一个自己没有的账号 —— 那种情况
  // 仍然弹 Key 设置框。判据在 `unauthorizedAction()`：看**刚才发的是哪种凭证**，
  // 而不是 store 里有没有 user（会话过期时 user 恰好是 null，按它判会判反）。
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (unauthorizedAction() === 'login') {
        clearSession();
        navigate('/login', {
          replace: true,
          state: { from: location.pathname + location.search },
        });
        return;
      }
      setKeyModal({ open: true, reason: 'API Key 无效或已过期，请重新填写' });
    });
    return () => setUnauthorizedHandler(null);
    // location 变化不需要重装回调：读的是回调触发那一刻的值。
    // 但依赖数组里写上它，是为了让「跳转时带的 from」是最新路径而不是挂载时的路径。
  }, [clearSession, navigate, location.pathname, location.search]);

  // 后端存活指示灯。30s 一次，只为让「接口全红」时用户知道是服务没起
  const { data: health, error: healthError } = usePolling(getHealth, 30_000);
  const online = health?.status === 'ok' && !healthError;

  return (
    // 收起态挂 --rail：内容区的横向留白要补回来（见 shell.css 的 --shell-gutter）
    <Layout className={`aigzl-shell${collapsed ? ' aigzl-shell--rail' : ''}`}>
      <Sider
        className={`aigzl-sider${collapsed ? ' aigzl-sider--collapsed' : ''}`}
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={SIDER_WIDTH}
        collapsedWidth={SIDER_COLLAPSED}
      >
        {/* 标记块取产品名前两个字母。原来写的 "AZ" 和 AIGZL 对不上，是个来路不明的缩写 */}
        <div className="aigzl-brand">
          <span className="aigzl-brand__mark">AI</span>
          {collapsed ? null : <span className="aigzl-brand__word">AIGZL</span>}
        </div>
        <SideNav collapsed={collapsed} />
      </Sider>

      <Layout>
        <header className="aigzl-header">
          <Button
            type="text"
            size="small"
            aria-label={collapsed ? '展开侧栏' : '收起侧栏'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleSider}
          />

          {/* 分两组：左边是「系统告诉你的」，右边是「你能操作的」。
              原来用 · 分隔点把它们串成一行，反而更碎，改成靠间距分组。 */}
          <div className="aigzl-header__right">
            <Tooltip title={online ? '后端服务正常' : '后端服务不可达'}>
              {/* 状态不能只靠颜色传达，所以徽标带文字 */}
              <Badge status={online ? 'success' : 'error'} text={online ? '在线' : '离线'} />
            </Tooltip>

            <div className="aigzl-header__actions">
              <ThemeSwitch />
              {/* 登录之后 API Key 就是纯机器身份了，不该继续占着顶栏。
                  没登录（本地开发 / 用 Key 调试）时才留这个入口。 */}
              {authUser ? null : (
                <Tooltip title="设置 API Key" placement="bottom">
                  <Button
                    type="text"
                    size="small"
                    aria-label="设置 API Key"
                    icon={<KeyOutlined />}
                    onClick={() => setKeyModal({ open: true })}
                  />
                </Tooltip>
              )}
              <UserMenu />
            </div>
          </div>
        </header>

        <Content className="aigzl-content">
          <ErrorBoundary scope="页面">
            <Suspense fallback={<Skeleton active style={{ padding: 24 }} />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Content>
      </Layout>

      <ApiKeyModal
        open={keyModal.open}
        reason={keyModal.reason}
        onClose={() => setKeyModal({ open: false })}
      />
    </Layout>
  );
}
